// Postmessage protocol that lets a sandboxed widget iframe sign Vault SDK
// requests with the parent vault page's connected wallet. The widget
// never sees the user's seed; it posts a request to `window.parent` and
// awaits a signature reply.
//
// Wire format is plain JSON; byte arrays serialize as `number[]` because
// postMessage does not structured-clone `Uint8Array` reliably across
// every origin/host pair (some sandboxing flags reject it).
//
// Two delivery quirks widget authors must handle:
//
// 1. Attach-vs-load race. The parent vault attaches its message listener
//    when the widget modal mounts, but the iframe's contentWindow is null
//    until after the first navigation completes. A widget that fires a
//    request from a top-level module body may post before the parent's
//    `allowedSource` check can resolve, and the message is silently
//    dropped. Widgets MUST retry with backoff (or wait for the first
//    successful reply) rather than pin a single `requestId` and assume
//    the response will arrive.
//
// 2. Stale-reply race. If the user closes the modal mid-flight (after the
//    widget posted a sign-request but before `wallet.sign()` resolved),
//    the parent's listener is torn down by the cleanup, but the in-flight
//    `wallet.sign()` still completes and the reply is posted to a now-
//    destroyed contentWindow. The browser drops it silently. The widget
//    should treat a missing response (no `requestId` match within a
//    timeout) the same as a denied signature.

export const WIDGET_SIGN_REQUEST_TYPE = 'cef-widget:sign-request';
export const WIDGET_SIGN_RESPONSE_TYPE = 'cef-widget:sign-response';
export const WIDGET_IDENTITY_REQUEST_TYPE = 'cef-widget:identity-request';
export const WIDGET_IDENTITY_RESPONSE_TYPE = 'cef-widget:identity-response';

export interface WidgetSignRequest {
  type: typeof WIDGET_SIGN_REQUEST_TYPE;
  requestId: string;
  bytes: number[];
}

export interface WidgetSignResponse {
  type: typeof WIDGET_SIGN_RESPONSE_TYPE;
  requestId: string;
  signature: number[] | null;
  error?: string;
}

export interface WidgetIdentityRequest {
  type: typeof WIDGET_IDENTITY_REQUEST_TYPE;
  requestId: string;
}

export interface WidgetIdentityResponse {
  type: typeof WIDGET_IDENTITY_RESPONSE_TYPE;
  requestId: string;
  pubkey: string;
  /**
   * Omitted on an error reply — the handler never had (or refused to use)
   * a real signer type to report at that point, so it doesn't fabricate
   * one. Present and honest (`signer.type` verbatim) on success.
   */
  sigType?: 'ed25519' | 'sr25519';
  error?: string;
}

export type WidgetBridgeRequest = WidgetIdentityRequest | WidgetSignRequest;
export type WidgetBridgeResponse = WidgetIdentityResponse | WidgetSignResponse;
export type WidgetSignMessage = WidgetBridgeRequest | WidgetBridgeResponse;

export function isWidgetSignMessage(value: unknown): value is WidgetSignMessage {
  if (!value || typeof value !== 'object') return false;
  const t = (value as { type?: unknown }).type;
  return (
    t === WIDGET_SIGN_REQUEST_TYPE
    || t === WIDGET_SIGN_RESPONSE_TYPE
    || t === WIDGET_IDENTITY_REQUEST_TYPE
    || t === WIDGET_IDENTITY_RESPONSE_TYPE
  );
}

/**
 * Upper bound on the byte array a sign request may carry. Lets us reject
 * obvious DoS / memory-pressure payloads before they reach any downstream
 * handler. 64KB is well above any realistic JCS-canonical payload (Vault
 * delegation tokens are <2KB) and well below anything that would stall a
 * browser tab.
 */
export const WIDGET_SIGN_MAX_BYTES = 64 * 1024;

export function isWidgetBridgeRequest(value: unknown): value is WidgetBridgeRequest {
  if (!isWidgetSignMessage(value)) return false;
  if (typeof value.requestId !== 'string' || value.requestId.length === 0) return false;
  if (value.type === WIDGET_IDENTITY_REQUEST_TYPE) return true;
  if (value.type === WIDGET_SIGN_REQUEST_TYPE) {
    return isValidByteArray((value as { bytes?: unknown }).bytes);
  }
  return false;
}

function isValidByteArray(value: unknown): value is number[] {
  if (!Array.isArray(value)) return false;
  if (value.length > WIDGET_SIGN_MAX_BYTES) return false;
  for (const n of value) {
    if (typeof n !== 'number') return false;
    if (!Number.isInteger(n)) return false;
    if (n < 0 || n > 255) return false;
  }
  return true;
}

// Wire conversions between the JSON-safe `number[]` carried over
// postMessage and the `Uint8Array` the wallet signs / returns. postMessage
// does not structured-clone `Uint8Array` reliably across every sandboxed
// origin pair, so the wire format is always a plain array.

export function bytesToArray(bytes: Uint8Array): number[] {
  return Array.from(bytes);
}

export function arrayToBytes(arr: number[]): Uint8Array {
  return Uint8Array.from(arr);
}
